// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import CartLineAvailability from "@gcg/App/Components/Cart/CartLineAvailability";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import { isProductInActive } from "@gcg/App/Utilities/helpers";
import ProductDescription, { ProductDescriptionStyles } from "@gcg/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@gcg/Overrides/Components/ProductPartNumbers";
import ProductPrice from "@gcg/Overrides/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@gcg/Overrides/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@gcg/Overrides/Widgets/Cart/CartLineQuantity";
import { screen } from "@gcg/XDL/Helpers";
import AddOutline from "@gcg/XDL/Icons/AddOutline";
import Delete from "@gcg/XDL/Icons/Delete";
import iconsObject from "@gcg/XDL/Icons/gcgCommonIcons";
import Measure from "@gcg/XDL/Icons/Measure";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button from "@insite/mobius/Button";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showRemoveAction?: boolean;
    showAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    isInStock?: boolean;
    isPartial?: boolean;
    isBackOrder?: boolean;
    editable?: boolean;
    showPONumber?: boolean;
    showDate?: boolean;
    isCustomGrouping?: boolean;
    showBadging?: boolean;
    qtyContent?: React.ReactNode;
    availabilityContent?: React.ReactNode;
    totalContent?: React.ReactNode;
    customerProductNumber?: string;
    productNumber?: string;
    showQtyOrderedShipped?: boolean;
    showAddToCart?: boolean;
    showShipmentData?: boolean;
    handleShowShipmentDataClick?: Function;
    productId?: string | null;
    qtyOne?: string;
    qtyTwo?: string;
    pricingOnDate?: boolean;
    showCutWire?: boolean;
    productIdAsId?: boolean;
    total?: string;
    dontShowCertification?: boolean;
    isSeacoastCustom?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    cartSettings: getSettingsCollection(state).cartSettings,
    productInfo: getProductInfoFromList(state, "orderDetails", props.productId as string),
    requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
    requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
    isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state)?.type === "CheckoutReviewAndSubmitPage",
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    customSettings: getSettingsCollection(state).customSettings,
    currentPage: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: InjectableCss;
    infoContainer?: InjectableCss;
    secondSection?: InjectableCss;
    productImageGridItem?: InjectableCss;
    lineNumberGridItem?: InjectableCss;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: InjectableCss;
    cartLineInfoContainer?: InjectableCss;
    productInfoGridItem?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productBrandAndDescriptionGridItem?: InjectableCss;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: InjectableCss;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: InjectableCss;
    quantityAndExtendedUnitNetPriceContainer?: InjectableCss;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceText?: TypographyProps;
    cartLineErrorMessageGridItem?: InjectableCss;
    cartLineErrorMessageText?: TypographyProps;
    middleContentAreaGridItem?: InjectableCss;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    cutWireLink?: LinkPresentationProps;
    cartLineNotesGridItem?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    wishListAndCheckboxes?: InjectableCss;
    certificationsStyles?: InjectableCss;
    certificationLabels?: CheckboxPresentationProps;
    certRequested?: InjectableCss;
    addToCartButtonWrapper?: InjectableCss;
    shipmentDataWrapper?: ClickablePresentationProps;
    shipmentDataIcon?: IconPresentationProps;
    shipmentDataText?: TypographyPresentationProps;
    ctaButtons?: InjectableCss;
    smallHeadingText?: SmallHeadingAndTextStyles;
    datePicker?: DatePickerPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    isRestrictedGridItem?: GridItemProps;
    isRestrictedText?: TypographyProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        css: css`
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
            padding: 10px;
            display: flex;
            flex-wrap: wrap;
            position: relative;
            width: 100%;

            @media print {
                border: 1px solid rgba(0, 0, 0, 0.1);
                box-shadow: none;

                &:not(:last-child) {
                    border-bottom: 0;
                }

                display: block;
                page-break-before: auto;
                page-break-after: auto;

                &,
                * {
                    break-inside: avoid;
                    page-break-inside: avoid;
                    column-break-inside: avoid;
                }
            }
            &:hover {
                && .remove-icon {
                    display: block;
                }
            }
        `,
    },
    infoContainer: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;

            ${screen("lte", "xs")`padding-left: 10px;`};

            @media print {
                display: table;
            }
        `,
    },
    secondSection: {
        css: css`
            flex: 1;

            @media (max-width: 800px) {
                width: 100% !important;
                padding-left: 170px; // to match the alignment with the description
                margin-top: 5px;
            }

            ${screen("lte", "xs")`padding: 0;`}

            @media print {
                display: table-row;
                > * {
                    padding-left: 170px;
                }
            }
        `,
    },
    productImageGridItem: {
        css: css`
            width: 150px;
            height: 150px;
            margin-right: 20px;
            overflow: hidden;

            ${screen("lte", "xs")`width: 100%; display: flex; justify-content: center;`};

            @media print {
                display: table-cell;
                max-width: 150px;
                float: left;
            }

            .product-image-print {
                max-width: 150px;
                max-height: 150px;

                @media screen {
                    display: none;
                }
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 150px;
                height: 150px;
                position: relative;

                @media print {
                    display: none;
                }
            `,
        },
        image: {
            css: css`
                width: auto;
                height: auto;
                max-width: 150px;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            `,
        },
    },
    cartLineInfoContainer: {
        css: css`
            width: calc(100% - 170px);
            @media (max-width: 481px) {
                width: 100%;
            }
            @media print {
                display: table-cell;
                max-width: calc(100% - 170px);
                vertical-align: top;
                float: left;
            }
        `,
    },
    productInfoGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${screen("lte", "xs")`flex-wrap: wrap;`}
            @media print {
                display: block;
            }
        `,
    },
    productInfoContainer: {},
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
    },
    productDescriptionGridItem: {
        css: css`
            height: 60px;
            margin-bottom: 16px;
            padding-right: 15px;
            ${screen("lte", "xs")`padding: 0;`}
        `,
    },
    productDescription: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: "16px",
                lineHeight: "20px",
            },
            css: css`
                &:hover > *,
                &:focus > * {
                    text-decoration: underline;
                    color: ${getColor("primary")} !important;
                }
            `,
        },
        productDetailDescription: {
            color: "text.main",
            size: "16px",
            lineHeight: "20px",
        },
    },
    configurationGridItem: {
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        css: css`
            width: 50%;
            ${screen("lte", "xs")`width: 100%;`}
            @media print {
                float: left;
            }
        `,
    },
    productPartNumbers: {
        container: { gap: 0 },
        erpNumberGridItem: {
            css: css`
                padding: 0;
                font-size: 14px;
                line-height: 18px;
            `,
        },
        customerNameGridItem: {
            css: css`
                padding: 0;
                font-size: 14px;
                line-height: 18px;
            `,
        },
        specNumberGridItem: {
            css: css`
                padding: 0;
                font-size: 14px;
                line-height: 18px;
            `,
        },
        specNumberLabelText: {
            weight: "bold",
        },
        manufacturerItemGridItem: {
            css: css`
                padding: 0;
                font-size: 14px;
                line-height: 18px;
            `,
        },
    },
    middleContentAreaGridItem: {
        css: css`
            width: 50%;
            padding-left: 10px;
            ${screen("lte", "xs")`width: 100%;padding:0;margin-top:10px;`}
            @media print {
                padding-left: 0;
                float: right;
            }
        `,
    },
    quantityAndExtendedUnitNetPriceContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 60px;
            margin-bottom: 16px;
            &.not-editable {
                align-items: flex-start;
            }
            &&& .qty-only {
                text-align: left;
                width: 80px;
            }
            @media (max-width: 800px) {
                height: auto;
                align-items: flex-end;
                &.not-editable {
                    align-items: flex-end;
                }
                &&& .qty-only {
                    text-align: left;
                }
            }
        `,
    },
    quantity: {
        editableQuantityTextField: {
            cssOverrides: {
                formInputWrapper: css`
                    & > * {
                        margin-top: 0 !important;
                    }
                    & label {
                        display: none;
                    }
                    & > input {
                        text-align: center;
                        width: 60px !important;
                    }

                    & input::-webkit-outer-spin-button,
                    & input::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                    }

                    & input[type="number"] {
                        -moz-appearance: textfield !important;
                    }
                `,
            },
        },
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    cartLineErrorMessageGridItem: {
        css: css`
            width: 100%;
            margin-bottom: 5px;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    wishListAndCheckboxes: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                flex-direction: column;
            }
        `,
    },
    certificationsStyles: {
        css: css`
            display: inline-flex;
            flex-direction: column;

            & > div {
                margin-bottom: 10px;
                font-size: 14px;
                line-height: 14px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            @media print {
                display: block;

                > div {
                    display: flex;
                }
            }
        `,
    },
    certificationLabels: {
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            css: css`
                margin-left: 5px !important;
            `,
        },
    },
    addToListLink: {
        color: "text.main",
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
        },
        icon: {
            iconProps: {
                src: AddOutline,
                size: 14,
                color: "primary.main",
            },
            position: "left",
        },
        css: css`
            margin-top: 0;
            margin-bottom: 10px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    cutWireLink: {
        color: "primary.main",
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
        },
        icon: {
            iconProps: {
                src: Measure,
                size: 16,
                color: "primary.main",
                css: css`
                    vertical-align: middle;
                    margin-right: 5px;
                `,
            },
            position: "left",
        },
        css: css`
            margin-top: 0;
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: {
        css: css`
            margin-left: 20px;

            @media (max-width: 800px) {
                width: 100%;
                padding-right: 5px;
                margin-left: 10px;
                margin-top: 10px;
            }

            &.no-value {
                display: none;
            }
        `,
    },
    removeCartLineGridItem: {
        css: css`
            display: none;
            position: absolute;
            right: 10px;
            bottom: 12px;

            @media (max-width: 1024px) and (min-width: 801px) {
                display: block !important;
            }

            @media (max-width: 800px) {
                display: block !important;
                top: 10px;
                right: 5px;
                bottom: auto;
            }
        `,
    },
    removeCartLineIcon: { src: Delete, color: "primary.main", size: 16 },
    lineNumberGridItem: {
        css: css`
            line-height: 20px;
        `,
    },
    certRequested: {
        css: css`
            display: flex;
            align-items: center;
            font-size: 14px;
            line-height: 20px;
            margin-top: 3px;
        `,
    },
    addToCartButtonWrapper: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    shipmentDataWrapper: {
        css: css`
            ${screen("lt", "sm")`
                 margin-bottom: 10px;
             `}

            &:hover,
             &:focus {
                .shipment-data-icon {
                    color: ${getColor("text.main")};
                }

                .shipment-data-text {
                    text-decoration: underline;
                }
            }
        `,
    },
    shipmentDataIcon: {
        src: iconsObject["Info"],
        size: 12,
        color: "primary.main",
        css: css`
            transition: all 200ms ease-in-out;
        `,
    },
    shipmentDataText: {
        size: "14px",
        lineHeight: "10px",
        color: "text.main",
        css: css`
            margin-left: 6.5px;
            transition: all 200ms ease-in-out;
        `,
    },
    ctaButtons: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            margin-right: 15px;
        `,
    },
    smallHeadingText: {
        heading: {
            css: css`
                display: none;
                @media (max-width: 800px) {
                    display: block;
                    font-size: 16px;
                }
            `,
        },
    },
    datePicker: {
        cssOverrides: {
            formInputWrapper: css`
                padding-bottom: 150px;
            `,
        },
    },
    isRestrictedGridItem: {
        width: 12,
    },
    isRestrictedText: {
        color: "danger",
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    showAddToList,
    isInStock,
    isPartial,
    isBackOrder,
    editable,
    showPONumber,
    showDate,
    cartSettings,
    isCustomGrouping,
    showBadging,
    qtyContent,
    availabilityContent,
    totalContent,
    customerProductNumber,
    productNumber,
    showQtyOrderedShipped,
    showAddToCart,
    showShipmentData,
    handleShowShipmentDataClick,
    productInfo,
    qtyOne,
    qtyTwo,
    pricingOnDate,
    showCutWire,
    productIdAsId,
    requestedDeliveryDate,
    requestedPickupDate,
    isPickUp,
    total,
    dontShowCertification,
    isCheckoutReviewAndSubmitPage,
    enableVat,
    vatPriceDisplay,
    customSettings,
    isSeacoastCustom,
    currentPage,
}) => {
    const [showCutWireButton, setShowCutWireButton] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const removeButtonRef = React.useRef<HTMLDivElement>(undefined as any);
    const [eisCertificate, setEisCertificate] = React.useState(cartLine.properties["eis/GCGCertification"] === "true");
    const [mfrCertificate, setMfrCertificate] = React.useState(cartLine.properties["mfrCertification"] === "true");
    const [isChangePONumerModalOpen, setChangePONumerModalOpen] = React.useState(false);
    const [isChangeDateModalOpen, setChangeDateModalOpen] = React.useState(false);
    const [poLineNumber, setPoLineNumber] = React.useState(cartLine.properties["poLineNumber"]);
    const dispatch = useDispatch();
    const [removeClicked, setRemoveClicked] = React.useState(false);

    const isOrderDetailsPage = currentPage === "OrderDetailsPage";

    const enableAuthenticatedExperience =
        (customSettings as any).enableAuthenticatedExperience.toLowerCase() === "true";

    React.useEffect(() => {
        if (cartLine.properties["allowCutWire"] === "true" && showCutWire) {
            setShowCutWireButton(true);
        }
    }, []);

    const oldDate = cartLine.properties["requestedByDate"]
        ? new Date(cartLine.properties["requestedByDate"])
        : isPickUp
        ? requestedPickupDate
        : requestedDeliveryDate;

    const [requestedDate, setRequestedDate] = React.useState(oldDate);

    let firstSectionWidth = css`
        width: 60%;
    `;
    let notEditableFirstSectionWidth = css`
        width: 62%;
    `;
    let columnWidth = css`
        width: calc(100% / 3);
    `;

    if (showQtyOrderedShipped) {
        firstSectionWidth = css`
            width: 55%;
        `;
        notEditableFirstSectionWidth = css`
            width: 57%;
        `;
        columnWidth = css`
            width: calc(100% / 4);
        `;
    }

    const firstSectionStyles = css`
        display: flex;
        ${firstSectionWidth};

        &.not-editable {
            ${notEditableFirstSectionWidth};
        }

        @media (max-width: 800px) {
            width: 100% !important;
        }

        ${screen("lte", "xs")`flex-wrap: wrap;`};

        @media print {
            width: 100% !important;
            display: table-row;
        }
    `;

    const columnStyles = css`
        ${columnWidth};
        text-align: center;

        &.not-editable.qty {
            padding-right: 11px;
        }

        &.cartPricing {
            flex-grow: 1;
        }

        &.total-column {
            text-align: right;
        }
    `;

    React.useEffect(() => {
        if (
            cartLine.properties?.hasOwnProperty("poLineNumber") &&
            poLineNumber !== cartLine.properties["poLineNumber"]
        ) {
            setPoLineNumber(cartLine.properties["poLineNumber"]);
        }
    }, [cartLine.properties["poLineNumber"]]);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
                cartId: cart.id,
            } as any);
        }
    };

    React.useEffect(() => {
        if (eisCertificate.toString() !== cartLine.properties["eis/GCGCertification"]) {
            setEisCertificate(cartLine.properties["eis/GCGCertification"] === "true");
        }
        if (mfrCertificate.toString() !== cartLine.properties["mfrCertification"]) {
            setMfrCertificate(cartLine.properties["mfrCertification"] === "true");
        }
    }, [cartLine]);

    const eisCertificationHandler = (event: React.SyntheticEvent, value: boolean) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    "eis/GCGCertification": value.toString(),
                },
            },
            cartId: cart.id,
        } as any);
    };

    const mfrCertificationHandler = (event: React.SyntheticEvent, value: boolean) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    mfrCertification: value.toString(),
                },
            },
            cartId: cart.id,
        } as any);
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                cartId: cart.id,
                shouldNotReloadPromotions: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            } as any);
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }
        setRemoveClicked(true);

        removeCartLine({ cartLineId: cartLine.id, cartId: cart.id } as any);
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });

            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const handleCloseChangePONumberModal = () => {
        setChangePONumerModalOpen(false);
    };

    const handleSetPoNumber = (event: any) => {
        const newValue = parseInt(event.target.value, 10);
        setPoLineNumber(newValue.toLocaleString());
    };

    const handleChangePoLineNumber = () => {
        if (poLineNumber !== cartLine.properties["poLineNumber"]) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        poLineNumber: poLineNumber?.toString(),
                    },
                },
                cartId: cart.id,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Number Updated"),
                        messageType: "success",
                    });
                },
            } as any);
        } else {
            setChangePONumerModalOpen(false);
        }
    };

    const handleCloseChangeDateModal = () => {
        setChangeDateModalOpen(false);
        setRequestedDate(oldDate);
    };

    const handleSetRequestedDate = ({ selectedDay }: DatePickerState) => {
        if (!selectedDay) {
            selectedDay = tomorrow;
        }
        const newDate = new Date(selectedDay!);
        setRequestedDate(newDate);
    };

    const handleChangeRequestedDate = () => {
        if (requestedDate?.getTime() !== new Date(cartLine.properties["requestedByDate"]).getTime()) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        requestedByDate: requestedDate,
                    },
                },
                cartId: cart.id,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Requested Date Updated"),
                        messageType: "success",
                    });
                },
            } as any);
        } else {
            setChangeDateModalOpen(false);
            setRequestedDate(oldDate);
        }
    };
    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
            });
        }
    };
    const openCutWireModal = () => {
        dispatch({
            type: "CutWire/SetModalOpened",
            isOpen: true,
            cartLine,
        });
    };

    const errorMessages: React.ReactNode[] = [];
    const badges = cartLine?.properties["badges"] ? JSON.parse(cartLine?.properties["badges"]) : null;

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (isProductInActive(cartLine)) {
        errorMessages.push(translate("Inactive product"));
    }

    const showRemoveButton = () => {
        removeButtonRef.current.style.display = "block";
    };

    const hideRemoveButton = () => {
        removeButtonRef.current.style.display = "none";
    };

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const lineNumber = (cartLine as any).linePOReference || (cartLine as any).lineNumber;
    const product = { ...cartLine };
    const unitPriceDisplay = (cartLine as any)?.unitPriceDisplay;
    const unitOfMeasure = (cartLine as any)?.unitOfMeasure;
    const realTimePricingUomString = (cartLine as any)?.properties?.realTimePricingUom;
    const pricePerString = (cartLine as any)?.properties?.pricePer;
    const realTimePricingUom = realTimePricingUomString && parseInt(realTimePricingUomString, 10);
    const pricePer = pricePerString && parseInt(pricePerString, 10);

    if (productIdAsId) {
        product["id"] = product["productId"] as string;
    }

    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);

    return (
        <StyledWrapper
            {...styles.container}
            onMouseEnter={showRemoveButton}
            onMouseLeave={hideRemoveButton}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.infoContainer}>
                <StyledWrapper css={firstSectionStyles} className={editable && !showDate ? "" : "not-editable"}>
                    <StyledWrapper {...styles.productImageGridItem}>
                        <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                        <img
                            src={
                                "product" in cartLine
                                    ? (cartLine as any).product.mediumImagePath ||
                                      (cartLine as any).product.smallImagePath
                                    : cartLine.smallImagePath || (cartLine as any).mediumImagePath
                            }
                            alt={"product" in cartLine ? (cartLine as any).product.imageAltText : cartLine.altText}
                            className="product-image-print"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.cartLineInfoContainer}>
                        <StyledWrapper {...styles.productDescriptionGridItem}>
                            <StyledWrapper
                                css={css`
                                    width: 100%;
                                `}
                            >
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                            </StyledWrapper>
                            <ProductDescription
                                product={cartLine}
                                showElipsesOn={cartLine.brand ? "2" : "3"}
                                showElipses={true}
                                extendedStyles={styles.productDescription}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.productInfoGridItem}>
                            <StyledWrapper {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    showLabel={true}
                                    showSpecNumber={true}
                                    showCustomerName={true}
                                    showManufacturerItem={true}
                                    showProductNumber={true}
                                    productNumber={
                                        productNumber ? (cartLine as any)[productNumber] : cartLine.erpNumber
                                    }
                                    customerProductNumber={
                                        customerProductNumber
                                            ? (cartLine as any)[customerProductNumber]
                                            : cartLine.customerName
                                    }
                                    manufacturerItem={cartLine.manufacturerItem}
                                    specNumber={cartLine.properties["specNumber"] || ""}
                                    extendedStyles={styles.productPartNumbers}
                                />
                                {cartLine.isRestricted && (
                                    <StyledWrapper {...styles.isRestrictedGridItem}>
                                        <Typography {...styles.isRestrictedText}>
                                            {translate("Restricted product")}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                {promotions && promotions.length > 0 && (
                                    <StyledWrapper>
                                        <Typography as="strong">{translate("Promotion:")}</Typography>
                                        {promotions.map(promotion => (
                                            <Typography {...styles.promotionNameText} key={promotion.id} as="span">
                                                {promotion.name},
                                            </Typography>
                                        ))}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.middleContentAreaGridItem}>
                                {showInventoryAvailability &&
                                    !cartLine.quoteRequired &&
                                    !availabilityContent &&
                                    (isInStock || isPartial || isBackOrder ? (
                                        <CartLineAvailability
                                            productId={cartLine.productId!}
                                            isInStock={isInStock}
                                            isPartial={isPartial}
                                            isBackOrder={isBackOrder}
                                            trackInventory={cartLine.trackInventory}
                                            additionalResults={cartLine.properties["totalInSelectedPlant"]}
                                            unitOfMeasure={cartLine.properties.baseUnitOfMeasure}
                                            extendedStyles={styles.productAvailability}
                                            fulfillmentMedthod={cart?.fulfillmentMethod.toLocaleLowerCase()}
                                            qtyOnHand={cartLine.qtyOnHand}
                                            isCustomGrouping={isCustomGrouping}
                                            qtyOrdered={cartLine.properties["qtyOrdered"]}
                                            inventoryFallBack={cartLine.properties["inventoryFallBack"]}
                                        />
                                    ) : (
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.properties.baseUnitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    ))}
                                {showBadging && (
                                    <StyledWrapper
                                        css={css`
                                            margin-top: 15px;
                                        `}
                                    >
                                        {badges &&
                                            badges.map(
                                                (
                                                    badge: {
                                                        Image: string | undefined;
                                                        DisplayName: string;
                                                        Description: string;
                                                    },
                                                    index: any,
                                                ) =>
                                                    badge.Image && (
                                                        <StyledWrapper
                                                            css={css`
                                                                display: inline;
                                                                margin-right: 5px;
                                                            `}
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            key={`badge${index}`}
                                                        >
                                                            <XPopup
                                                                type="tooltip"
                                                                trigger={
                                                                    <StyledWrapper
                                                                        css={css`
                                                                            display: inline;
                                                                        `}
                                                                    >
                                                                        <LazyImage
                                                                            src={badge.Image}
                                                                            width="20px"
                                                                            height="20px"
                                                                        />
                                                                    </StyledWrapper>
                                                                }
                                                                css={css`
                                                                    max-width: 250px;
                                                                `}
                                                                position="top right"
                                                                fixed
                                                            >
                                                                <StyledWrapper className="popup-content-wrapper">
                                                                    {/* eslint-disable react/no-danger */}
                                                                    <div
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                badge.DisplayName === "PROP_65_CANCER"
                                                                                    ? cartLine.properties.prop65Text ||
                                                                                      (siteMessage(
                                                                                          "Custom_Prop65DefaultText",
                                                                                      ) as string)
                                                                                    : badge.Description,
                                                                        }}
                                                                    />
                                                                </StyledWrapper>
                                                            </XPopup>
                                                        </StyledWrapper>
                                                    ),
                                            )}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>

                            {availabilityContent && (
                                <StyledWrapper {...styles.middleContentAreaGridItem}>
                                    {availabilityContent}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.secondSection} className={editable && !showDate ? "" : "not-editable"}>
                    <StyledWrapper
                        {...styles.quantityAndExtendedUnitNetPriceContainer}
                        className={editable && !showDate ? "" : "not-editable"}
                    >
                        <StyledWrapper
                            css={columnStyles}
                            className={`${!qtyContent ? "qty-only" : ""} ${editable ? "" : "not-editable qty"}`}
                        >
                            {!qtyContent && (
                                <>
                                    <CartLineQuantity
                                        showLabel={true}
                                        cart={cart}
                                        editable={editable && !showDate && !errorMessages.length}
                                        onQtyOrderedChange={qtyOrderedChangeHandler}
                                        extendedStyles={styles.quantity}
                                        qtyDataName={qtyOne}
                                        label={showQtyOrderedShipped ? translate("Qty Ordered") : translate("Qty")}
                                    />
                                </>
                            )}
                            {qtyContent && qtyContent}
                        </StyledWrapper>
                        {!qtyContent && showQtyOrderedShipped && (
                            <StyledWrapper css={columnStyles}>
                                <CartLineQuantity
                                    showLabel={showQtyOrderedShipped}
                                    cart={cart}
                                    editable={editable && !showDate && !errorMessages.length}
                                    onQtyOrderedChange={qtyOrderedChangeHandler}
                                    extendedStyles={styles.quantity}
                                    qtyDataName={qtyTwo}
                                    label={showQtyOrderedShipped ? translate("Qty Shipped") : translate("Qty")}
                                />
                            </StyledWrapper>
                        )}
                        {!cart?.cartNotPriced && !isSeacoastCustom && cartLine.pricing && (
                            <>
                                <StyledWrapper
                                    css={columnStyles}
                                    className={`${cart && pricingOnDate === undefined ? "cartPricing" : ""} ${
                                        editable && !showDate ? "" : "not-editable"
                                    }`}
                                >
                                    {(cartLine.quoteRequired || cartLine.pricing?.unitNetPrice === 0) && (
                                        <Typography data-test-selector="cartline_QuoteQuantityUOM">
                                            {cartLine.properties["qtyOrdered"]
                                                ? cartLine.properties["qtyOrdered"]
                                                : cartLine["qtyOrdered"]}{" "}
                                            / {unitOfMeasure}
                                        </Typography>
                                    )}
                                    {cart && pricingOnDate !== undefined && pricingOnDate && (
                                        <ProductPrice
                                            showLabel={false}
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={true}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            showPricePer={true}
                                            showUnitPriceDisplay={true}
                                            showInvalidPriceMessage={true}
                                        />
                                    )}
                                    {cart && pricingOnDate !== undefined && !pricingOnDate && "----"}
                                    {cart && pricingOnDate === undefined && (
                                        <ProductPrice
                                            showLabel={false}
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={true}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            showUOM={true}
                                            showInvalidPriceMessage={true}
                                            extendedStyles={
                                                cartLine.quoteRequired || cartLine.pricing?.unitNetPrice === 0
                                                    ? ({
                                                          wrapper: {
                                                              css: css`
                                                                  white-space: normal;
                                                              `,
                                                          },
                                                      } as any)
                                                    : ""
                                            }
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper
                                    css={columnStyles}
                                    className={editable && !showDate ? "" : "not-editable"}
                                >
                                    {pricingOnDate !== undefined && !pricingOnDate && "----"}
                                    {!cartLine.quoteRequired &&
                                        cartLine.pricing?.unitNetPrice !== 0 &&
                                        !cart?.cartNotPriced &&
                                        !totalContent &&
                                        (pricingOnDate === undefined || pricingOnDate) && (
                                            <>
                                                <SmallHeadingAndText
                                                    heading={translate("Total")}
                                                    text={" "}
                                                    extendedStyles={styles.smallHeadingText}
                                                />
                                                <Typography
                                                    {...styles.extendedUnitNetPriceText}
                                                    data-test-selector="cartline_extendedUnitNetPrice"
                                                >
                                                    {enableVat && vatPriceDisplay !== "DisplayWithoutVat" ? (
                                                        cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    ) : (
                                                        <>
                                                            {cartLine.properties["qtyOrdered"]
                                                                ? `${cart.currencySymbol}${(
                                                                      parseInt(cartLine.properties["qtyOrdered"], 10) *
                                                                      cartLine.pricing!.unitNetPrice
                                                                  ).toFixed(2)}`
                                                                : cartLine.pricing!.extendedUnitNetPriceDisplay}
                                                        </>
                                                    )}
                                                </Typography>
                                                {enableVat && (
                                                    <>
                                                        <Typography as="p" {...styles.vatLabelText}>
                                                            {vatPriceDisplay === "DisplayWithVat" ||
                                                            vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                                ? `${translate("Inc. VAT")} (${
                                                                      cartLine.pricing.vatRate
                                                                  }%)`
                                                                : translate("Ex. VAT")}
                                                        </Typography>
                                                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                            <>
                                                                <Typography {...styles.subtotalWithoutVatText}>
                                                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                                </Typography>
                                                                <Typography as="p" {...styles.vatLabelText}>
                                                                    {translate("Ex. VAT")}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    {totalContent && totalContent}
                                </StyledWrapper>
                            </>
                        )}
                        {!cart?.cartNotPriced && !isSeacoastCustom && !cartLine.pricing && (
                            <>
                                <StyledWrapper css={columnStyles}>
                                    {unitPriceDisplay}
                                    {(unitOfMeasure || (!!pricePer && pricePer > 1)) && ` / `}
                                    {!!pricePer && pricePer > 1 && (
                                        <>
                                            {pricePer}
                                            &nbsp;
                                        </>
                                    )}
                                    {unitOfMeasure && (
                                        <>
                                            {!!realTimePricingUom && realTimePricingUom > 1 && realTimePricingUom}
                                            {unitOfMeasure}
                                        </>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper css={columnStyles} className="total-column">
                                    {total ? (cartLine as any)[total] : (cartLine as any).extendedUnitNetPriceDisplay}
                                </StyledWrapper>
                            </>
                        )}
                        {cart?.cartNotPriced && !isSeacoastCustom && (
                            <Typography
                                size="14px"
                                lineHeight="16px"
                                css={css`
                                    flex: 1 0 auto;
                                    text-align: center;
                                `}
                            >
                                {siteMessage("RealTimePricing_PriceLoadFailed")}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.wishListAndCheckboxes}>
                        {errorMessages.length > 0 && (
                            <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                                {errorMessages.map((message, index) => (
                                    <Typography
                                        {...styles.cartLineErrorMessageText}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        data-test-selector="cartline_errorMessage"
                                    >
                                        {message}
                                    </Typography>
                                ))}
                            </StyledWrapper>
                        )}
                        {(showAddToList ||
                            showCutWireButton ||
                            (showAddToCart && productInfo) ||
                            (showShipmentData && handleShowShipmentDataClick)) &&
                            !errorMessages.length && (
                                <StyledWrapper {...styles.ctaButtons}>
                                    {showCutWireButton && (
                                        <Link {...styles.cutWireLink} onClick={openCutWireModal}>
                                            {cartLine.properties?.hasOwnProperty("cutWire") &&
                                            cartLine.properties["cutWire"] !== "" &&
                                            JSON.parse(cartLine.properties["cutWire"]).length > 0
                                                ? translate("Edit Cut Lengths")
                                                : translate("Add Cut Lengths")}
                                        </Link>
                                    )}
                                    {showAddToList && cartLine.canAddToWishlist && enableAuthenticatedExperience && (
                                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                            {translate("Shopping List")}
                                        </Link>
                                    )}
                                    {showAddToCart && productInfo && (
                                        <ProductContext.Provider
                                            value={...{
                                                product,
                                                productInfo,
                                            } as any}
                                        >
                                            <StyledWrapper {...styles.addToCartButtonWrapper}>
                                                {isOrderDetailsPage ? (
                                                    <ProductAddToCartButton
                                                        {...({ asLink: true } as any)}
                                                        withIcon={true}
                                                        // xng change: GCG-676: changing add to cart link name
                                                        labelOverride={translate("Re-Buy or Re-Quote")}
                                                    />
                                                ) : (
                                                    <ProductAddToCartButton
                                                        {...({ asLink: true } as any)}
                                                        withIcon={true}
                                                    />
                                                )}
                                            </StyledWrapper>
                                        </ProductContext.Provider>
                                    )}
                                    {showShipmentData && handleShowShipmentDataClick && (
                                        <Clickable
                                            {...styles.shipmentDataWrapper}
                                            onClick={() => {
                                                handleShowShipmentDataClick(cartLine.id);
                                            }}
                                        >
                                            <Icon {...styles.shipmentDataIcon} className="shipment-data-icon" />
                                            <Typography {...styles.shipmentDataText} className="shipment-data-text">
                                                {translate("Shipment Data")}
                                            </Typography>
                                        </Clickable>
                                    )}
                                </StyledWrapper>
                            )}
                        {!errorMessages.length && (
                            <StyledWrapper {...styles.certificationsStyles}>
                                {!dontShowCertification && (
                                    <>
                                        <Checkbox
                                            checked={eisCertificate}
                                            data-test-selector={`line_eis_certificate_${cartLine.id}`}
                                            disabled={!editable}
                                            onChange={eisCertificationHandler}
                                            {...styles.certificationLabels}
                                        >
                                            {siteMessage("Custom_site_request_certification")}
                                        </Checkbox>
                                        <Checkbox
                                            checked={mfrCertificate}
                                            disabled={!editable}
                                            data-test-selector={`line_mfr_certificate_${cartLine.id}`}
                                            onChange={mfrCertificationHandler}
                                            {...styles.certificationLabels}
                                        >
                                            {translate("Request Mfr. Certification")}
                                        </Checkbox>
                                    </>
                                )}
                                {showDate && (
                                    <StyledWrapper>
                                        <Typography as="strong">
                                            {translate(isPickUp ? "Requested Pick Up Date:" : "Requested Ship Date:")}
                                        </Typography>
                                        <Typography
                                            css={css`
                                                margin-left: 5px;
                                            `}
                                        >
                                            {cartLine.properties["requestedByDate"] ? (
                                                new Date(cartLine.properties["requestedByDate"]).toLocaleDateString()
                                            ) : requestedDeliveryDate || requestedPickupDate ? (
                                                <LocalizedDateTime
                                                    dateTime={isPickUp ? requestedPickupDate : requestedDeliveryDate}
                                                />
                                            ) : (
                                                translate("Not Set")
                                            )}
                                        </Typography>
                                        {editable && (
                                            <Link
                                                onClick={() => setChangeDateModalOpen(true)}
                                                css={css`
                                                    margin-left: 5px;
                                                `}
                                            >
                                                {translate("Edit")}
                                            </Link>
                                        )}
                                    </StyledWrapper>
                                )}
                                {showPONumber && cartLine.line && (
                                    <StyledWrapper>
                                        <Typography as="strong">{translate("PO Line #:")}</Typography>
                                        <Typography
                                            css={css`
                                                margin-left: 5px;
                                            `}
                                        >
                                            {cartLine.properties["poLineNumber"]}
                                        </Typography>
                                        {editable && (
                                            <Link
                                                css={css`
                                                    margin-left: 5px;
                                                `}
                                                onClick={() => setChangePONumerModalOpen(true)}
                                            >
                                                {translate("Edit")}
                                            </Link>
                                        )}
                                    </StyledWrapper>
                                )}
                                {showPONumber && lineNumber && (
                                    <StyledWrapper>
                                        <Typography as="strong">{translate("PO Line #:")}</Typography>
                                        <Typography
                                            css={css`
                                                margin-left: 5px;
                                            `}
                                        >
                                            {lineNumber}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {cart?.showCostCode && !cartLine.isPromotionItem && (
                <StyledWrapper {...styles.costCodeGridItem}>
                    <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                    {cart.canEditCostCode ? (
                        <Select value={cartLine.costCode} onChange={costCodeChangeHandler} {...styles.costCodeSelect}>
                            {cart.costCodes?.map(costCode => (
                                <option key={costCode.costCode} value={costCode.costCode}>
                                    {costCode.description}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper
                {...styles.cartLineNotesGridItem}
                className={!editable && !cartLine.notes.length ? "no-value" : ""}
            >
                {showLineNotes && !cartLine.isPromotionItem && cart?.properties["isPunchout"] === undefined && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable && !errorMessages.length}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                        showCutWire={!showCutWire}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.removeCartLineGridItem} ref={removeButtonRef}>
                {showRemoveAction && editable && !removeClicked && !isCheckoutReviewAndSubmitPage && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
            <Modal
                headline={translate("Change PO Line Number")}
                sizeVariant="small"
                isOpen={isChangePONumerModalOpen}
                handleClose={handleCloseChangePONumberModal}
            >
                <TextField label="PO Number" type="number" value={poLineNumber || 0} onChange={handleSetPoNumber} />
                <StyledWrapper className="modal-actions">
                    <Button variant="secondary" onClick={handleCloseChangePONumberModal}>
                        {translate("Close")}
                    </Button>
                    <Button variant="primary" onClick={handleChangePoLineNumber}>
                        {translate("Save")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                headline={translate("Change Requested Date")}
                isOpen={isChangeDateModalOpen}
                handleClose={handleCloseChangeDateModal}
                sizeVariant="small"
            >
                <DatePicker
                    label={translate("Requested On")}
                    selectedDay={requestedDate!}
                    onDayChange={handleSetRequestedDate}
                    dateTimePickerProps={{
                        minDate: tomorrow,
                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                    }}
                    {...styles.datePicker}
                />

                <StyledWrapper className="modal-actions">
                    <Button variant="secondary" onClick={handleCloseChangeDateModal}>
                        {translate("Close")}
                    </Button>
                    <Button variant="primary" disabled={!requestedDate} onClick={handleChangeRequestedDate}>
                        {translate("Save")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
